//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import { CSSTransition } from "react-transition-group";
// event-scheduler-attendee-input imports
import { IContainer, IProps, IState } from "./EventSchedulerAttendeeInput.d";
import { eventSchedulerAttendeeInputFragments } from "./EventSchedulerAttendeeInput.gql";
import * as s from "./EventSchedulerAttendeeInput.styles";
// other imports
import { FeedSchedulerInput } from "@components/feed-scheduler-input";
import { InfoTip } from "@components/tool-tip";
// state
import { IReduxState } from "@reducers/root.reducer";
// util
import { niceNameArrayFromPersons } from "@util/event.util";
import { track, TrackingEvents } from "@util/analytics.util";
// material-ui
import { H2 } from "@ui";
import { People, ChevronLeft, Edit } from "@material-ui/icons";
import { Button } from "@clockwise/design-system";

//////////////////
// COMPONENT
//////////////////
class EventSchedulerAttendeeInputCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      editMode: false,
      calendarIds: props.calendarIds || [],
    };
  }

  private expand = (expand: boolean) => {
    if (expand) {
      track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_ATTENDEE_INPUT_CLICKED, {
        schedulingSessionId: this.props.schedulingSessionId,
      }); // analytics
    }
    this.setState({ editMode: expand });
  };

  private onSchedulerInputChange = (calendarIds: string[]) => {
    this.setState({ calendarIds });
    if (this.props.onChange) {
      this.props.onChange(calendarIds);
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderViewMode = (persons: ISchema.IPerson[]) => {
    const niceNamesArray = niceNameArrayFromPersons(persons, [], 45, true, undefined, true);

    return (
      <div
        className="cw-flex cw-flex-row cw-items-center cw-py-2 cw-cursor-pointer"
        onClick={() => this.expand(true)}
      >
        <People {...s.people} />
        <div style={s.attendeeList}>
          {niceNamesArray.map((name, i) => {
            const person = persons.find(
              (p) => p.profile && `${p.profile.givenName} ${p.profile.familyName}` === name,
            );
            return (
              <span key={`${name}${i}`}>
                {person ? (
                  <InfoTip
                    targetElement={
                      <span style={{ ...s.attendeeList, cursor: "pointer" }}>{name}</span>
                    }
                    text={person.primaryEmail}
                    tipType={"info"}
                    iconStyle={{ display: "inline" }}
                    targetElementContainerStyles={{ display: "inline" }}
                  />
                ) : (
                  <span>{name}</span>
                )}
              </span>
            );
          })}
        </div>
        <Button
          size="small"
          variant="text"
          startIcon={Edit}
          onClick={() => this.expand(true)}
          cw-id="edit-event-attendees-button"
        />
      </div>
    );
  };

  private renderEditMode = (persons: ISchema.IPerson[]) => {
    return (
      <div style={s.editModeContainer}>
        <div className="cw-flex cw-items-center cw-gap-1">
          <Button
            variant="text"
            startIcon={ChevronLeft}
            onClick={() => this.expand(false)}
            cw-id="attendee-input-back-button"
          />
          <H2 style={s.title}>Choose attendees</H2>
        </div>
        <FeedSchedulerInput
          environment={this.props.relay.environment}
          orgId={this.props.orgId}
          queryInput={""}
          excludeCalendars={[]}
          initialPersons={persons}
          selectedPersons={[]}
          onChange={this.onSchedulerInputChange}
        />
        <div style={s.buttonContainer}>
          <Button sentiment="positive" onClick={() => this.expand(false)} fullWidth>
            Done
          </Button>
        </div>
      </div>
    );
  };

  public render() {
    const { personsByCalendarIdsErrorable } = this.props;
    const { editMode } = this.state;

    const persons =
      personsByCalendarIdsErrorable.__typename === "PersonList"
        ? personsByCalendarIdsErrorable.list
        : [];

    return (
      <div>
        <CSSTransition in={editMode} timeout={250} classNames="slide-up" unmountOnExit>
          {this.renderEditMode(persons)}
        </CSSTransition>
        {this.renderViewMode(persons)}
      </div>
    );
  }
}

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
  };
}

const EventSchedulerAttendeeInputRedux = connect(
  mapStateToProps,
  {},
)(EventSchedulerAttendeeInputCmpt);

//////////////////
// RELAY
//////////////////
export const EventSchedulerAttendeeInput = createFragmentContainer<IContainer>(
  EventSchedulerAttendeeInputRedux,
  eventSchedulerAttendeeInputFragments,
);
